import { AppContext } from "App/AppContext";
import { ErrorHandler } from "UI/Errors";
import { ApplicationStorage, AppStorageItemType } from "../../../../../OW.Remoting/Remoting/ApplicationStorage";
import { OWObject } from "../../../../../OW.Remoting/Remoting/DataTypes/OWObject";
import { ExportManager } from "../../../../../OW.Remoting/Remoting/ExportManager";
import { ActionContext, ActionType } from "../../../../UI.Controls/Actions/IAction";
import { MenuType } from "../../../../UI.Controls/Actions/MenuType";
import { ControlParams } from "../../Controls/ControlInterfaces";
import { IValidationMaster } from "../FormControl";
import { IMasterableFrame } from "../Frames/FrameInterfaces";
import { FrameSharedMethods } from "../Frames/FrameSharedMethods";
import { XGAction } from "./XGAction";

export class ReportAction extends XGAction {
    private _closeForm: boolean = false;
    private _reportName: string;
    private _selectedLanguage: string;
    private _selectedLanguageCombo: string;
    private _selectedFormat: string;
    private _showSettings: boolean = false;
    private _validateIf: boolean = false;
    private _actionType: ActionType;

    constructor(params: ControlParams) {
        super(params);
        this.element.classList.add("MethodAction");
        this._showSettings = this.getBoolDataAttr("ShowSettings", AppContext.instance.configuration.defaultShowSettings);
        this._reportName = this.element.getAttribute("data-reportname");
        this.enabledVisual = true;
        this.setToMenu(MenuType.mtPrint);
    }

    public set selectedLangInCombo(value: string) {
        this._selectedLanguageCombo = value;
    }

    public get selectedLangInCombo(): string {
        return this._selectedLanguageCombo;
    }

    public set selectedLanguage(value: string) {
        this._selectedLanguage = value;
    }

    public get selectedLanguage(): string {
        return this._selectedLanguage;
    }

    public set selectedFormat(value: string) {
        this._selectedFormat = value;
    }

    public get selectedFormat(): string {
        return this._selectedFormat;
    }

    public set validateIf(val: boolean) {
        this._validateIf = val;
    }

    public get validateIf(): boolean {
        return this._validateIf;
    }

    public set closeForm(val: boolean) {
        this._closeForm = val;
    }

    public get closeForm(): boolean {
        return this._closeForm;
    }

    public override get context(): ActionContext {
        return ActionContext.objectAction;
    }

    public override get actionType(): ActionType {
        return this._actionType || ActionType.report; // Pokud není nastavena explicitně, vrací se typ report
    }

    public override set actionType(value: ActionType) {
        this._actionType = value;
    }

    public get showSettings(): boolean {
        return this._showSettings;
    }

    private async _checkBeforeExecute(): Promise<boolean> {
        const savingParams = FrameSharedMethods.convertRequestContextToSavingParams(this._getRequestContext());

        // zkontroluje se uložení změn na owneru a jeho nadřízených framech
        if (!this.owner || !(await this._checkAndSaveForm(savingParams)))
            return false;

        if (this.owner instanceof IValidationMaster && this.validateIf) {
            const result = await this.owner.validateAndShowMessage();
            if (!result)
                return false;
        }
        return true;
    }

    protected override async _internalExecute(): Promise<void> {
        if (await this._checkBeforeExecute()) {
            try {
                const reportType = ApplicationStorage.get(AppStorageItemType.reportType);
                this._selectedLanguage = ApplicationStorage.get(AppStorageItemType.reportLanguage);
                this._print(reportType);
            } catch (e) {
                ErrorHandler.instance.handleError(e);
            }
        }
    }

    public async print(selectedLang: string, reportType: string): Promise<void> {
        try {
            if (await this._checkBeforeExecute()) {
                this._selectedLanguage = selectedLang;
                await this._print(reportType);
            }
        } catch (e) {
            ErrorHandler.instance.handleError(e);
        }
    }

    private async _print(reportType: string): Promise<void> {
        let instanceID = "";

        let actObject: OWObject;

        if (this.owner instanceof IMasterableFrame) //akce je definována na ListFrame (nebo některém jeho bratranci)
            actObject = (this.owner as IMasterableFrame).actualObject;
        else if (this.owner.masterFrame instanceof IMasterableFrame) //akce je definována na DetailFrame
            actObject = this.owner.masterFrame.actualObject;
        else
            actObject = this.owner.actualObject;

        if (actObject != null)
            instanceID = actObject.instanceID;

        if (instanceID) {
            ExportManager.instance.printReport(instanceID, this._reportName, this._selectedLanguage, reportType, this.ownerForm && this.ownerForm.readVersioned, this._getRequestContext());

            if (this.closeForm) {
                this.ownerForm.close();
            }
        }

        await this?.action();
    }
}
